import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { DropZoneProps } from './Types'
import { DragEventHandler } from 'react'
import { Vec2d, VecLike, createShapeId, track } from '@tldraw/tldraw'
import { DraggableShapeProps } from 'components/Design/DraggableShape/Types'
import { FrameShape } from 'components/VisualStudioPlayground/Shapes/FrameShape/Types'
import { handleImageDrop } from './handleImageDrop'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useInsertMediaFile } from 'components/Design/utils/useInsertMediaFile'

const DropZone = track(
  observer(({ children }: DropZoneProps) => {
    const editor = useEditor()
    const { designStore } = useRootStore()
    const insertMediaFile = useInsertMediaFile()

    const handleDrop: DragEventHandler<HTMLDivElement> = async (ev) => {
      if (ev.dataTransfer.files.length > 0) {
        return
      }
      const dropPoint = editor.screenToPage({
        x: ev.clientX,
        y: ev.clientY,
      })

      // Find the frame the shape was dropped on
      const targetFrame = editor.getShapesAtPoint(dropPoint).find((s): s is FrameShape => s.type === 'frame')
      if (!targetFrame) return

      const payload = JSON.parse(ev.dataTransfer.getData('payload')) as DraggableShapeProps['payload']

      // Drag offset is the offset from the top left of the shape to the mouse position when the drag started.
      // We also need to scale the offset by the inverse of the zoom level, so that the shape doesn't jump.
      let dragOffset: VecLike = JSON.parse(ev.dataTransfer.getData('dragOffset'))
      dragOffset = Vec2d.From(dragOffset).mul(1 / editor.getZoomLevel())

      // We then subtract this from the drop point to get the correct position for the shape.
      // Finally, we subtract the frame's position so that the shape is positioned relative to the frame. (tldraw uses relative positioning for shapes that are children of frames).
      const localDropPoint = Vec2d.From(dropPoint).sub(dragOffset).sub({ x: targetFrame.x, y: targetFrame.y })

      const { type, props } = payload

      if (type === 'image') {
        if (payload.special_type === 'media_file' && payload.file.mime_type !== 'image/svg+xml') {
          designStore?.setProcessingShapeId(targetFrame.id)
          insertMediaFile(
            editor,
            {
              mediaFile: payload.file,
              transformations: {},
            },
            {
              replaceReplaceable: true,
            },
          )
          designStore?.setProcessingShapeId(undefined)
          return
        }

        handleImageDrop(editor, localDropPoint, targetFrame, payload)
        return
      }

      const id = createShapeId()
      editor.createShape({
        id,
        parentId: targetFrame.id,
        x: localDropPoint.x,
        y: localDropPoint.y,
        type,
        props,
      })
      editor.select(id)
    }

    return <div onDrop={handleDrop}>{children}</div>
  }),
)

export { DropZone }
